import { useEffect, useMemo, useState } from 'react';
import {
  dealDetailSchema,
  toWireDate,
  type Deal,
  type DealDetail,
  type DealReason,
} from '@forecastify/shared';
import { Button, Dialog, Select, Switch, TextInput, Textarea } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { Field } from '../card';
import { DateField } from '../date-field';
import { dealRefusal, type DealFeeds } from './deal-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D5 · *Megnyert* and *Elvesztett* — `phase6/11`, `12`. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Each posts itself (`POST /deals/:id/won` / `lost`) and hands the answer back, so the record, the
 * list and the board all close a deal the same way. A refusal stays in the dialog — the deal was
 * not closed, and a toast over a dialog that then vanishes would say so to nobody.
 */
export interface WonDialogProps {
  readonly open: boolean;
  readonly deal: Deal;
  readonly feeds: DealFeeds;
  readonly onClose: () => void;
  /** `generate`: the *continue with generation* box was ticked. */
  readonly onDone: (detail: DealDetail, generate: boolean) => void;
}

/** `"21000000.00"` → `21000000` — what a person would type, not what the wire carries. */
function amountText(value: string): string {
  const amount = Number(value);
  return Number.isFinite(amount) ? String(amount) : '';
}

function parseAmount(text: string): number | null {
  const amount = Number(text.replace(/\s/gu, '').replace(',', '.'));
  return text.trim() === '' || !Number.isFinite(amount) || amount < 0 ? null : amount;
}

/** One close request: the answer parsed, or the refusal's sentence. */
function useClosePost() {
  const t = useT();
  const session = useSession();
  return async (path: string, body: unknown): Promise<DealDetail | string> => {
    const response = await session
      .request(path, { method: 'POST', body: JSON.stringify(body) })
      .catch(() => null);
    if (response === null) return t('error.unavailable');
    if (!response.ok) return dealRefusal(response, t);
    const parsed = dealDetailSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : t('error.validation_failed');
  };
}

export function WonDialog({ open, deal, feeds, onClose, onDone }: WonDialogProps) {
  const t = useT();
  const post = useClosePost();
  const reasons = useMemo(
    () => (feeds.config?.reasons ?? []).filter((reason) => reason.kind === 'won' && reason.active),
    [feeds.config],
  );

  const [value, setValue] = useState('');
  const [closedOn, setClosedOn] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [note, setNote] = useState('');
  const [generate, setGenerate] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValue(amountText(deal.value));
    setClosedOn(toWireDate(new Date()));
    setReasonId('');
    setNote('');
    setGenerate(true);
    setError(null);
  }, [open, deal.value]);

  const submit = async (): Promise<void> => {
    const amount = parseAmount(value);
    if (amount === null) {
      setError(t('deals.error.value'));
      return;
    }
    if (closedOn === '') {
      setError(t('dealWon.errorDate'));
      return;
    }
    setBusy(true);
    const answer = await post(`/deals/${deal.id}/won`, {
      value: amount,
      closedOn,
      reasonId: reasonId === '' ? null : reasonId,
      note: note.trim(),
    });
    setBusy(false);
    if (typeof answer === 'string') {
      setError(answer);
      return;
    }
    onDone(answer, generate);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('dealWon.title')}
      description={`${deal.identifier} · ${deal.name}`}
      closeLabel={t('common.close')}
      width={512}
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('dealWon.value')} required>
            <TextInput
              autoFocus
              inputMode="decimal"
              className="tabular-nums"
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                setError(null);
              }}
            />
          </Field>
          <Field label={t('dealWon.closedOn')} required>
            <DateField
              value={closedOn}
              clearable={false}
              onChange={setClosedOn}
              label={t('dealWon.closedOn')}
            />
          </Field>
        </div>
        <Field label={t('dealWon.reason')} note={t('dealWon.reasonHint')}>
          <Select
            value={reasonId}
            onChange={(event) => {
              setReasonId(event.target.value);
            }}
            options={[
              { value: '', label: t('dealWon.reasonNone') },
              ...reasons.map((reason) => ({ value: reason.id, label: reason.name })),
            ]}
          />
        </Field>
        <Field label={t('dealWon.note')}>
          <Textarea
            rows={2}
            maxLength={2000}
            value={note}
            placeholder={t('dealWon.notePlaceholder')}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </Field>
        <div className="flex items-start gap-2.5 rounded-lg border p-3">
          <Switch checked={generate} onCheckedChange={setGenerate} label={t('dealWon.generate')} />
          <span className="min-w-0 flex-1">
            <span className="block text-[12.5px] font-medium">{t('dealWon.generate')}</span>
            <span className="text-muted-foreground block text-[11.5px] leading-relaxed">
              {t('dealWon.generateHint')}
            </span>
          </span>
        </div>

        {error === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error}
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-2">
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" disabled={busy} onClick={() => void submit()}>
            {t('dealWon.submit')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export interface LostDialogProps {
  readonly open: boolean;
  readonly deal: Deal;
  readonly feeds: DealFeeds;
  readonly onClose: () => void;
  readonly onDone: (detail: DealDetail) => void;
}

/** Active lost reasons, grouped — the group rides as the option's right-hand hint. */
function lostReasons(feeds: DealFeeds): readonly DealReason[] {
  return (feeds.config?.reasons ?? [])
    .filter((reason) => reason.kind === 'lost' && reason.active)
    .sort(
      (a, b) => a.groupName.localeCompare(b.groupName, 'hu') || a.name.localeCompare(b.name, 'hu'),
    );
}

export function LostDialog({ open, deal, feeds, onClose, onDone }: LostDialogProps) {
  const t = useT();
  const post = useClosePost();
  const reasons = useMemo(() => lostReasons(feeds), [feeds]);

  const [reasonId, setReasonId] = useState('');
  const [competitor, setCompetitor] = useState('');
  const [closedOn, setClosedOn] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* ⚠️ The list row is a `Deal`, the record is a `DealDetail` — only the latter knows a
     competitor, so the field prefills when the dialog was opened from the record. */
  const knownCompetitor = 'competitor' in deal ? (deal as DealDetail).competitor : '';

  useEffect(() => {
    if (!open) return;
    setReasonId('');
    setCompetitor(knownCompetitor);
    setClosedOn(toWireDate(new Date()));
    setNote('');
    setError(null);
  }, [open, knownCompetitor]);

  const reason = reasons.find((candidate) => candidate.id === reasonId) ?? null;
  const noteRequired = reason?.needsNote === true;

  const submit = async (): Promise<void> => {
    if (reason === null) {
      setError(t('dealLost.errorReason'));
      return;
    }
    if (noteRequired && note.trim() === '') {
      setError(t('dealLost.errorNote'));
      return;
    }
    if (closedOn === '') {
      setError(t('dealWon.errorDate'));
      return;
    }
    setBusy(true);
    const answer = await post(`/deals/${deal.id}/lost`, {
      reasonId: reason.id,
      competitor: competitor.trim(),
      closedOn,
      note: note.trim(),
    });
    setBusy(false);
    if (typeof answer === 'string') {
      setError(answer);
      return;
    }
    onDone(answer);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('dealLost.title')}
      description={`${deal.identifier} · ${deal.name}`}
      closeLabel={t('common.close')}
      width={512}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealLost.reason')} required>
          <Select
            searchable
            searchPlaceholder={t('dealLost.reasonSearch')}
            value={reasonId}
            onChange={(event) => {
              setReasonId(event.target.value);
              setError(null);
            }}
            options={[
              { value: '', label: t('dealLost.reasonPick') },
              ...reasons.map((entry) => ({
                value: entry.id,
                label: entry.name,
                ...(entry.groupName === '' ? {} : { hint: entry.groupName }),
              })),
            ]}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('dealLost.competitor')}>
            <TextInput
              value={competitor}
              maxLength={120}
              placeholder={t('dealLost.competitorPlaceholder')}
              onChange={(event) => {
                setCompetitor(event.target.value);
              }}
            />
          </Field>
          <Field label={t('dealLost.closedOn')} required>
            <DateField
              value={closedOn}
              clearable={false}
              onChange={setClosedOn}
              label={t('dealLost.closedOn')}
            />
          </Field>
        </div>
        <Field
          label={t('dealLost.note')}
          required={noteRequired}
          {...(noteRequired ? { note: t('dealLost.noteRequired') } : {})}
        >
          <Textarea
            rows={2}
            maxLength={2000}
            value={note}
            placeholder={t('dealLost.notePlaceholder')}
            onChange={(event) => {
              setNote(event.target.value);
              setError(null);
            }}
          />
        </Field>

        {error === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error}
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-2">
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" size="lg" disabled={busy} onClick={() => void submit()}>
            {t('dealLost.submit')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
